import { useMutation } from "@vue/apollo-composable";
import { CREATE_ORDER } from "@/common/apollo/mutations";
import { BigNumber, ethers } from "ethers";
import { useWallet, Wallet } from "@/common/composables/useWallet";
import {
  DATA_V1_TYPE,
  EIP712_DOMAIN_TEMPLATE,
  EIP712_ORDER_TYPE,
  EIP712_ORDER_TYPES,
} from "@/common/constants/eip712";
import { EXCHANGE } from "@/common/constants";
import { inject } from "vue";

export const ORDERS_CONTEXT = Symbol();

export interface Orders {
  getAsset: (
    contract: string,
    tokenId: number | string,
    value: number | string
  ) => any;
  createOrder: (order: any, wallet: Wallet) => any;
}

export function createOrders(): Orders {
  const getAsset = (
    contract: string,
    tokenId: number | string,
    value: number | string
  ) => ({
    assetType: {
      assetClass: "ERC721",
      contract: ethers.utils.getAddress(contract),
      tokenId: BigNumber.from(tokenId),
    },
    value: BigNumber.from(value),
  });

  const _encodeData = (data: any) => {
    const encoded = ethers.utils.defaultAbiCoder.encode([DATA_V1_TYPE], [data]);
    return ["0x23d235ef", encoded];
  };

  const _assetToStruct = (asset: any) => {
    return {
      assetType: {
        assetClass: ethers.utils
          .keccak256(ethers.utils.toUtf8Bytes("ERC721"))
          .substring(0, 10),
        data: ethers.utils.defaultAbiCoder.encode(
          ["address", "uint256"],
          [asset.assetType.contract, asset.assetType.tokenId]
        ),
      },
      value: asset.value,
    };
  };

  const signOrder = (order: any, wallet: Wallet) => {
    const [data, dataType] = _encodeData(order.orderData);
    const domain = {
      ...EIP712_DOMAIN_TEMPLATE,
      verifyingContract: EXCHANGE,
      chainId: 4,
    };

    const orderMessage = {
      maker: order.maker,
      makeAsset: _assetToStruct(order.make),
      taker: order.taker ?? ethers.constants.AddressZero,
      takeAsset: _assetToStruct(order.take),
      salt: order.salt,
      start: order.start ?? 0,
      end: order.end ?? 0,
      dataType,
      data,
    };

    const signature = wallet.signTypedData({
      primaryType: EIP712_ORDER_TYPE,
      domain,
      types: EIP712_ORDER_TYPES,
      message: orderMessage,
    });

    return { ...order, signature };
  };

  const createOrder = (order: any, wallet: Wallet) => {
    order.type = "WHITELABEL_MARKETPLACE_V1";
    order.salt = BigNumber.from(ethers.utils.randomBytes(16)).toHexString();
    order.orderData = {
      dataType: `${order.type}_DATA_V1`,
      payouts: [],
      originFees: [],
      isMakeFill: true,
    };
    const signedOrder = signOrder(order, wallet);
    const { mutate: createOrder } = useMutation(CREATE_ORDER, signedOrder);
    return createOrder();
  };

  return { getAsset, createOrder };
}

export function useOrders(): Orders {
  const context = inject(ORDERS_CONTEXT) as Orders;

  return context;
}
